"use client";

import React, { useEffect, useRef, useState } from "react";
import { HeartHandshake, Leaf, LockOpen, Sparkles, UserCheck } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const benefitsData = [{
  title: "No Cages, Ever",
  description: "Your pet stays calm and comfortable in a private, cage‑free environment.",
  icon: LockOpen
}, {
  title: "No Car Rides. No Waiting",
  description: "We come to you—no stressful trips or crowded salons.",
  icon: HeartHandshake
}, {
  title: "1‑on‑1 Personalized Care",
  description: "Your pet gets our full attention from start to finish.",
  icon: UserCheck
}, {
  title: "Quiet, Low‑Stress Environment",
  description: "Our vans are designed to reduce noise and anxiety.",
  icon: Sparkles
}, {
  title: "Gentle on Skin & Coat",
  description: "We use high‑quality, environmentally safe products that are gentle, effective, and safe for your pet’s skin and coat.",
  icon: Leaf
}];
export default function TheBenefitsOfMobileGrooming() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="the-benefits-of-mobile-grooming" className="relative w-full overflow-hidden bg-background py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2 lg:gap-24">
          
          {}
          <div ref={imageRef} className={`relative mx-auto w-full max-w-md transition-all duration-1000 ease-out lg:max-w-none ${isImageVisible ? "translate-x-0 opacity-100" : "-translate-x-12 opacity-0"}`}>
            {}
            <div className="relative aspect-square w-full rounded-[40%_60%_70%_30%/40%_50%_60%_50%] border-4 border-dashed border-primary/60 p-3 transition-transform duration-700 hover:scale-[1.02]">
              <div className="h-full w-full overflow-hidden rounded-[40%_60%_70%_30%/40%_50%_60%_50%] bg-background">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600414/img/golden_retriever_drying.png" alt="Golden Retriever being hand-dried in a mobile grooming spa" className="h-full w-full object-cover object-center" />
              </div>
            </div>
          </div>

          {}
          <div className="flex flex-col justify-center">
            <div ref={headerRef} className={`mb-12 transition-all duration-700 ease-out ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
              <h2 className="font-sans text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
                The Benefits of <br className="hidden lg:block" />
                <span className="text-primary">Mobile Grooming</span>
              </h2>
            </div>

            <div className="flex flex-col gap-10">
              {benefitsData.map((benefit, i) => {
              const {
                ref: itemRef,
                isVisible: isItemVisible
              } = useRevealOnScroll<HTMLDivElement>({
                threshold: 0.1
              });
              return <div key={benefit.title} data-index={i} ref={itemRef} className={`group flex items-start gap-6 transition-all duration-700 ease-out ${isItemVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"} ${benefit.title === "Gentle on Skin & Coat" ? "-mb-6" : ""}`} style={{
                transitionDelay: `${i * 150}ms`
              }}>
                    <div className="flex shrink-0 items-center justify-center rounded-2xl bg-background p-4 shadow-sm transition-transform duration-300 group-hover:scale-110 group-hover:shadow-md">
                      <benefit.icon className="h-12 w-12 text-chart-2" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-sans text-xl font-bold text-foreground transition-colors group-hover:text-primary">
                        {benefit.title}
                      </h3>
                      <p className="mt-2 text-base leading-relaxed text-muted-foreground">
                        {benefit.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}